<template>
  <ModalWithHeaderAndBody
    @close="$emit('close')"
    title="Email Reviewers"
    :disable-backdrop-click="false"
    :custom-classes="['max-w-5xl']"
  >
    <template #default="modalProps">
      <BaseFetchData :action="loadData" v-slot="fetchDataProps">
        <div v-if="emailSent" class="space-y-4">
          <h3 class="text-base font-medium">Email was sent succesfully</h3>
          <BaseButton primary @click="modalProps.close"> Close </BaseButton>
        </div>
        <div v-else>
          <BaseVeeForm
            v-if="fetchDataProps.status.resolved"
            :action="submit"
            ensure-error-keys-camel-case
            v-slot="formProps"
            class="space-y-8"
          >
            <div class="space-y-8">
              <ValidationProvider name="from" slim vid="from" rules="required">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormLabel> From </BaseFormLabel>
                    <div>
                      <BaseFormSelect
                        v-model="formData.from"
                        :options="fromOptions"
                        :disabled="formProps.isPending"
                      />
                    </div>
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>

              <ValidationProvider name="to" slim vid="to" rules="required">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormLabel> To </BaseFormLabel>
                    <VSelect
                      v-model="formData.to"
                      :options="reviewerOptions"
                      label="fullName"
                      :reduce="(reviewer) => reviewer.id"
                      multiple
                      :disabled="formProps.isPending"
                      class="vue-select custom-option"
                    >
                      <template #option="{ fullName }">
                        <div class="flex group px-4 py-2 items-center">
                          <div class="ml-3">
                            <p class="text-sm font-medium text-gray-900 group-hover:text-white">
                              {{ fullName }}
                            </p>
                          </div>
                        </div>
                      </template>
                    </VSelect>
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>

              <ValidationProvider name="subject" slim vid="subject" rules="required">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormLabel> Subject </BaseFormLabel>
                    <BaseFormInput
                      v-model="formData.subject"
                      type="text"
                      :disabled="formProps.isPending"
                    />
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>

              <ValidationProvider name="body" slim vid="body">
                <template #default="{ errors }">
                  <BaseFormGroup>
                    <BaseFormLabel> Body </BaseFormLabel>
                    <VTextEditor
                      :custom-editor-classes="['min-h-64']"
                      :content="formData.body"
                      :read-only="formProps.isPending"
                      @input="formData.body = $event"
                    />
                    <input type="hidden" name="body" :value="formData.body" />
                    <BaseFormInlineMessage v-if="errors.length" danger>
                      {{ errors[0] }}
                    </BaseFormInlineMessage>
                  </BaseFormGroup>
                </template>
              </ValidationProvider>

              <div v-if="reviewerOptions.length" class="space-y-4">
                <BaseFormLabel> Reviewed Project Assets </BaseFormLabel>
                <div class="shadow-inner p-6 bg-gray-100 rounded">
                  <ProjectAssetsByReviewerList
                    :selected-reviewer-ids="formData.to"
                    :project-asset-ids="projectAssetIds"
                    :entities="entities"
                    @approval-request-revoked="fetchProjectAssets"
                  />
                </div>
              </div>
            </div>
            <div>
              <BaseButton primary :disabled="formProps.invalid || formProps.isPending">
                Send Email <Loader v-if="formProps.isPending" small inverse />
              </BaseButton>
            </div>
          </BaseVeeForm>
        </div>
      </BaseFetchData>
    </template>
  </ModalWithHeaderAndBody>
</template>

<script>
import ModalWithHeaderAndBody from 'Components/ModalWithHeaderAndBody.vue';
import VTextEditor from 'Components/Common/VTextEditor.vue';
import { normalize } from 'normalizr';
import { accountThirdPartyServicesClient, projectProjectAssetsClient } from '@/api/clients';
import mapKeysToCamelCase from '@/utils/map-keys-to-camel-case';
import projectAssetSchema from '@/store/entities/schemas/projectAssetSchema';
import ProjectAssetsByReviewerList from './emailReviewersModal/ProjectAssetsByReviewerList.vue';

const getDefaultBody = (
  project
) => `Υπάρχει έτοιμο υλικό που αφορά το project **${project.name}** και απαιτεί την αξιολόγησή σας.

Παρακαλώ ακολουθείστε το σύνδεσμο και υποβάλλετε την αξιολόγησή σας.`;

export default {
  name: 'EmailReviewersModal',
  components: {
    ModalWithHeaderAndBody,
    ProjectAssetsByReviewerList,
    VTextEditor,
  },
  props: {
    project: {
      type: Object,
      required: true,
    },
    defaultData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      thirdPartyServices: [],
      entities: {},
      projectAssetIds: [],
      formData: {
        from: null,
        to: this.defaultData?.to || [],
        subject: `Αίτημα έγκρισης για project ${this.project.name}`,
        body: getDefaultBody(this.project),
      },
      emailSent: false,
    };
  },
  computed: {
    fromOptions() {
      return this.thirdPartyServices.map((thirdPartyService) => ({
        value: thirdPartyService.config.fromEmail,
        label: `${thirdPartyService.config.fromName} (${thirdPartyService.config.fromEmail})`,
      }));
    },
    reviewerOptions() {
      const allReviewerIds = Object.keys(this.entities?.projectAssets || {}).reduce(
        (reviewerIds, projectAssetId) => [
          ...reviewerIds,
          ...(this.entities.projectAssets[projectAssetId]?.reviewers || []).filter(
            (reviewer) => typeof reviewer === 'string'
          ),
        ],
        []
      );

      return Array.from(new Set(allReviewerIds))
        .map((reviewerId) => this.entities?.users[reviewerId])
        .filter(Boolean);
    },
  },
  watch: {
    reviewerOptions(reviewerOptions) {
      this.formData.to = [
        ...this.formData.to.filter(
          (id) => reviewerOptions.findIndex((reviewerOption) => reviewerOption.id === id) >= 0
        ),
      ];
    },
  },
  methods: {
    async loadData() {
      try {
        await Promise.all([this.fetchThirdPartyServices(), this.fetchProjectAssets()]);

        this.setupDefaults();
      } catch (error) {
        //
      }
    },
    async fetchThirdPartyServices() {
      try {
        const response = await accountThirdPartyServicesClient.list(this.project.accountUuid, {
          params: {
            filter: {
              channel: 'mail',
            },
          },
        });

        this.thirdPartyServices = mapKeysToCamelCase(response.data.data, true);
      } catch (error) {
        //
      }
    },
    async fetchProjectAssets() {
      try {
        const response = await projectProjectAssetsClient.list(this.project.uuid, {
          params: {
            filter: {
              pending_approval: true,
            },
            include: 'approvalRequests,reviewers,asset.assetable',
          },
        });

        const { result, entities } = normalize(response.data.data, [projectAssetSchema]);

        this.projectAssetIds = result;
        this.entities = { ...entities };
      } catch (error) {
        //
      }
    },
    setupDefaults() {
      this.formData.from = this.fromOptions?.[0]?.value || null;

      if (!this.formData.to.length) {
        this.formData.to = this.reviewerOptions.map((reviewer) => reviewer.id);
      }
    },
    async submit() {
      try {
        await projectProjectAssetsClient.notifyReviewers(this.project.uuid, this.formData);

        this.emailSent = true;

        this.$emit('email-sent');

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
